import React, { useState, useRef } from 'react';
import { User, Mail, Phone, Camera, Eye, EyeOff, Lock, Upload, FileSpreadsheet } from 'lucide-react';
import Layout from '../components/Layout';
import { toast } from 'react-hot-toast';
import api from '../components/api';

const SettingPage = () => {
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const [settings, setSettings] = useState({
    darkMode: false,
    notifications: true,
    autoBackup: false,
    language: 'fr',
    compressionLevel: 'medium'
  });
  const fileInputRef = useRef(null);

  // Nouveaux états pour les modales
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [userInfo, setUserInfo] = useState({
    username: '',
    email: '',
    telephone: '',
    image: null
  });
  const [passwords, setPasswords] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const imageInputRef = useRef(null);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importErrors, setImportErrors] = useState([]);

  // Pour télécharger la sauvegarde
  const handleBackup = async () => {
    try {
      setLoading(true);
      const response = await api.get('/collect/backup/', {
        responseType: 'blob'
      });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.setAttribute('download', `sind-backup-${date}.sql`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Mettre à jour la date de dernière sauvegarde
      setLastBackup(new Date().toLocaleString());
      toast.success('Sauvegarde téléchargée avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  // Pour restaurer depuis un fichier
  const handleRestore = async (e) => {
    e.preventDefault();
    try {
      if (!fileInputRef.current.files[0]) {
        toast.error('Aucun fichier sélectionné');
        return;
      }

      const file = fileInputRef.current.files[0];
      if (!file.name.endsWith('.sql')) {
        toast.error('Le fichier doit être au format SQL');
        return;
      }

      // Créer un FormData pour l'envoi du fichier
      const formData = new FormData();
      formData.append('backup_file', file);

      setRestoring(true);
      await api.post('/collect/restore/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Restauration effectuée avec succès. Veuillez vous reconnecter.');
      // Rediriger vers la page de connexion après 3 secondes
      setTimeout(() => {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de la restauration:', error);
      toast.error('Erreur lors de la restauration');
    } finally {
      setRestoring(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    toast.success(`Paramètre ${key} mis à jour`);
  };

  // Gestionnaire pour la mise à jour du profil
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('username', userInfo.username);
      formData.append('email', userInfo.email);
      formData.append('telephone', userInfo.telephone);
      if (userInfo.image) {
        formData.append('image', userInfo.image);
      }

      await api.patch('/users/profile/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Profil mis à jour avec succès');
      setShowProfileModal(false);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du profil');
    }
  };

  // Gestionnaire pour le changement de mot de passe
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    try {
      if (passwords.newPassword !== passwords.confirmPassword) {
        toast.error('Les mots de passe ne correspondent pas');
        return;
      }

      await api.post('/users/change-password/', {
        old_password: passwords.oldPassword,
        new_password: passwords.newPassword
      });

      toast.success('Mot de passe modifié avec succès');
      setShowPasswordModal(false);
      setPasswords({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error('Erreur lors du changement de mot de passe');
    }
  };

  // Gestionnaire pour le changement d'image
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUserInfo({ ...userInfo, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await api.post('/collect/import-excel/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult(response.data);
      if (response.data.error_count > 0) {
        setImportErrors(response.data.errors);
      } else {
        setImportErrors([]);
      }
      
      toast.success(response.data.message);
      setImportFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Erreur lors de l\'importation:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'importation');
    } finally {
      setImporting(false);
    }
  };

  const InputWithIcon = ({ icon: Icon, ...props }) => (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Icon className="h-5 w-5 text-gray-400" />
      </div>
      <input
        {...props}
        className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg 
          focus:ring-2 focus:ring-purple-500 focus:border-purple-500
          placeholder:text-gray-400 text-gray-900 text-sm
          transition-all duration-200 ease-in-out
          hover:border-gray-400"
      />
    </div>
  );

  const PasswordInput = ({ icon: Icon, show, onToggle, ...props }) => (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Icon className="h-5 w-5 text-gray-400" />
      </div>
      <input
        {...props}
        type={show ? "text" : "password"}
        className="block w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-lg 
          focus:ring-2 focus:ring-purple-500 focus:border-purple-500
          placeholder:text-gray-400 text-gray-900 text-sm
          transition-all duration-200 ease-in-out
          hover:border-gray-400"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
      >
        {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
      </button>
    </div>
  );

  return (
    <Layout>
      <div className="container px-6 mx-auto">
        <h2 className="my-6 text-2xl font-semibold text-gray-700">Paramètres</h2>
        
        <div className="grid gap-6 mb-8 md:grid-cols-2">
          {/* Sauvegarde et restauration */}
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <div className="flex items-center mb-6">
              <div className="w-8 h-8 text-purple-600 mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-700">Base de données</h3>
            </div>
            
            <div className="mb-6">
              <h4 className="text-lg font-medium text-gray-600 mb-3">Sauvegarde</h4>
              <p className="text-gray-600 mb-4">
                Téléchargez une copie de sauvegarde de toutes vos données au format SQL.
              </p>
              <button 
                className={`flex items-center justify-center w-full px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-purple-600 border border-transparent rounded-lg active:bg-purple-600 hover:bg-purple-700 focus:outline-none focus:shadow-outline-purple ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
                onClick={handleBackup}
                disabled={loading}
              >
                {loading ? (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                  </svg>
                )}
                {loading ? 'Sauvegarde en cours...' : 'Télécharger la sauvegarde'}
              </button>
              {lastBackup && (
                <div className="mt-2 text-sm text-gray-500">
                  Dernière sauvegarde: {lastBackup}
                </div>
              )}
            </div>
            
            <div>
              <h4 className="text-lg font-medium text-gray-600 mb-3">Restauration</h4>
              <p className="text-gray-600 mb-4">
                Restaurez vos données à partir d'un fichier de sauvegarde SQL. 
                <span className="text-red-500 font-medium"> Attention: cette action remplacera toutes les données actuelles!</span>
              </p>
              <div className="flex flex-col space-y-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".sql"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button 
                  className={`flex items-center justify-center w-full px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-purple-600 border border-transparent rounded-lg active:bg-purple-600 hover:bg-purple-700 focus:outline-none focus:shadow-outline-purple ${restoring ? 'opacity-75 cursor-not-allowed' : ''}`}
                  onClick={handleRestore}
                  disabled={restoring}
                >
                  {restoring ? (
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                    </svg>
                  )}
                  {restoring ? 'Restauration en cours...' : 'Restaurer les données'}
                </button>
              </div>
            </div>
          </div>
          
          {/* Paramètres généraux */}
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <div className="flex items-center mb-6">
              <div className="w-8 h-8 text-purple-600 mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-700">Paramètres généraux</h3>
            </div>
            
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                    <span className="font-medium">Profil utilisateur</span>
                  </div>
                  <p className="text-sm text-gray-500">Modifier vos informations personnelles</p>
                </div>
                <button onClick={() => setShowProfileModal(true)} className="px-3 py-1 text-sm font-medium leading-5 text-purple-600 border border-purple-600 rounded-md hover:text-white hover:bg-purple-600">
                  Modifier mon profil
                </button>
              </div>
              
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                    </svg>
                    <span className="font-medium">Changer le mot de passe</span>
                  </div>
                  <p className="text-sm text-gray-500">Mettre à jour votre mot de passe</p>
                </div>
                <button onClick={() => setShowPasswordModal(true)} className="px-3 py-1 text-sm font-medium leading-5 text-purple-600 border border-purple-600 rounded-md hover:text-white hover:bg-purple-600">
                  Changer mon mot de passe
                </button>
              </div>
              
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                    </svg>
                    <span className="font-medium">Notifications</span>
                  </div>
                  <p className="text-sm text-gray-500">Gérer les notifications système</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer"
                    checked={settings.notifications}
                    onChange={() => handleSettingChange('notifications', !settings.notifications)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center">
                    <FileSpreadsheet className="w-5 h-5 mr-2" />
                    <span className="font-medium">Importation de données</span>
                  </div>
                  <p className="text-sm text-gray-500">Importer des données depuis un fichier Excel</p>
                </div>
                <button 
                  onClick={() => setShowImportModal(true)}
                  className="px-3 py-1 text-sm font-medium leading-5 text-purple-600 border border-purple-600 rounded-md hover:text-white hover:bg-purple-600"
                >
                  Importer des données
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modale de modification du profil */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Modifier le profil</h3>
              <button onClick={() => setShowProfileModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleProfileUpdate} className="space-y-6">
              <div className="flex justify-center">
                <div className="relative">
                  <img
                    src={imagePreview || userInfo.image || '/default-avatar.png'}
                    alt="Profile"
                    className="w-28 h-28 rounded-full object-cover ring-4 ring-purple-50"
                  />
                  <button
                    type="button"
                    onClick={() => imageInputRef.current.click()}
                    className="absolute -bottom-2 -right-2 p-2 bg-purple-600 rounded-full text-white 
                      hover:bg-purple-700 transform transition-transform hover:scale-110
                      shadow-lg"
                  >
                    <Camera className="h-5 w-5" />
                  </button>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom d'utilisateur</label>
                  <InputWithIcon
                    icon={User}
                    type="text"
                    placeholder="Votre nom d'utilisateur"
                    value={userInfo.username}
                    onChange={(e) => setUserInfo({ ...userInfo, username: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <InputWithIcon
                    icon={Mail}
                    type="email"
                    placeholder="votre@email.com"
                    value={userInfo.email}
                    onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <InputWithIcon
                    icon={Phone}
                    type="tel"
                    placeholder="+227 XX XX XX XX"
                    value={userInfo.telephone}
                    onChange={(e) => setUserInfo({ ...userInfo, telephone: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8">
                <button
                  type="button"
                  onClick={() => setShowProfileModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 
                    rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 
                    rounded-lg hover:bg-purple-700 transition-colors duration-200"
                >
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modale de changement de mot de passe */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Changer le mot de passe</h3>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ancien mot de passe</label>
                <PasswordInput
                  icon={Lock}
                  placeholder="Votre ancien mot de passe"
                  value={passwords.oldPassword}
                  onChange={(e) => setPasswords({ ...passwords, oldPassword: e.target.value })}
                  show={showOldPassword}
                  onToggle={() => setShowOldPassword(!showOldPassword)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
                <PasswordInput
                  icon={Lock}
                  placeholder="Votre nouveau mot de passe"
                  value={passwords.newPassword}
                  onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                  show={showNewPassword}
                  onToggle={() => setShowNewPassword(!showNewPassword)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe</label>
                <PasswordInput
                  icon={Lock}
                  placeholder="Confirmez le nouveau mot de passe"
                  value={passwords.confirmPassword}
                  onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                  show={showConfirmPassword}
                  onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                  required
                />
              </div>

              <div className="flex justify-end gap-3 mt-8">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 
                    rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 
                    rounded-lg hover:bg-purple-700 transition-colors duration-200"
                >
                  Changer le mot de passe
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modale d'importation Excel */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Importer des données depuis Excel</h3>
              <button 
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportResult(null);
                  setImportErrors([]);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }} 
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleImport} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fichier Excel (.xlsx ou .xls)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-full file:border-0
                      file:text-sm file:font-semibold
                      file:bg-purple-50 file:text-purple-700
                      hover:file:bg-purple-100"
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Le fichier doit contenir les colonnes suivantes : commune, superficie, population
                </p>
              </div>

              {importResult && (
                <div className={`p-4 rounded-lg ${importResult.error_count > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <p className={`text-sm ${importResult.error_count > 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {importResult.message}
                  </p>
                </div>
              )}

              {importErrors.length > 0 && (
                <div className="max-h-60 overflow-y-auto">
                  <h4 className="text-sm font-medium text-red-700 mb-2">Erreurs détaillées :</h4>
                  <ul className="text-sm text-red-600 space-y-1">
                    {importErrors.map((error, index) => (
                      <li key={index} className="border-b border-red-100 py-1">{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-8">
                <button
                  type="button"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                    setImportResult(null);
                    setImportErrors([]);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 
                    rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={!importFile || importing}
                  className={`px-4 py-2 text-sm font-medium text-white bg-purple-600 
                    rounded-lg hover:bg-purple-700 transition-colors duration-200
                    ${(!importFile || importing) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {importing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Importation en cours...
                    </>
                  ) : (
                    'Importer'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SettingPage;
